/* eslint-disable */
import { connect } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { BaseModal } from '../BaseModal';
import { ListTable } from '../../ListTable';
import { BackdropLoader } from '../../Loaders';
import { modalActions } from '@/Actions';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';

// BE row shape (fmcg_packaging_helpers.getPackageChildActivities):
//   { id, qrCode, stockId, storeId, variantId, movementType, activityDate,
//     qty, postUpdateStock, stockUpdated, reason, refId, refType,
//     shortInfo, activityBy, isActiveStock }
//
// isActiveStock=true means activity.stockId is currently mapped to the
// package (mapping.isDeleted == False). Rendered as a small green dot
// next to the QR code so the operator can distinguish current-children
// activities from historical-children activities.

const TABLE_COLUMNS = [
	{ title: 'Child QR', config: { type: 'DATA' } },
	{ title: 'Movement', config: { type: 'DATA' } },
	{ title: 'Qty', config: { type: 'DATA' } },
	{ title: 'Post Stock', config: { type: 'DATA' } },
	{ title: 'Reason', config: { type: 'DATA' } },
	{ title: 'Reference', config: { type: 'DATA' } },
	{ title: 'Note', config: { type: 'DATA' } },
	{ title: 'Activity Date', config: { type: 'DATA' } },
	{ title: 'Activity By', config: { type: 'DATA' } },
];

const COLMAP = {
	'Child QR': 'qrCode',
	Movement: 'movementType',
	Qty: 'qty',
	'Post Stock': 'postUpdateStock',
	Reason: 'reason',
	Reference: 'refType',
	Note: 'shortInfo',
	'Activity Date': 'activityDate',
	'Activity By': 'activityBy',
};

const ChildQrActivitiesModal = (props) => {
	const { show, data, toggleModal } = props;
	const packageId = data?.packageId;
	const packageQr = data?.packageQr;

	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [counts, setCounts] = useState({ activeStockCount: 0, totalChildCount: 0 });
	// Multi-select chip state: empty Set ⇒ show all child QRs.
	// Toggling a chip adds/removes its qrCode from this set.
	const [selectedQrs, setSelectedQrs] = useState(() => new Set());

	const handleClose = () => {
		setRows([]);
		setCounts({ activeStockCount: 0, totalChildCount: 0 });
		setSelectedQrs(new Set());
		toggleModal(false, {});
	};

	useEffect(() => {
		if (!show || !packageId) return;
		const fetchActivities = async () => {
			setLoading(true);
			// Reset chip selection when opening for a new package so the
			// previous filter doesn't leak across opens.
			setSelectedQrs(new Set());
			try {
				const response = await inventoryAnalyticsServices.fmcgPackageChildActivities({
					data: { packageId },
				});
				if (response?.responseCode === 'SS-001') {
					setRows(response?.data ?? []);
					setCounts({
						activeStockCount: response?.activeStockCount ?? 0,
						totalChildCount: response?.totalChildCount ?? 0,
					});
				} else {
					setRows([]);
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log('ChildQrActivitiesModal fetch error', error);
				setRows([]);
			} finally {
				setLoading(false);
			}
		};
		fetchActivities();
	}, [show, packageId]);

	// Build the deduped child-QR list from the activity rows. Each chip
	// also tracks whether ANY activity of that QR is currently active
	// (isActiveStock=true) — used to highlight currently-in-pack QRs.
	// Sorted: active first, then by qrCode ascending for stable display.
	const childQrChips = useMemo(() => {
		const map = new Map();
		(rows || []).forEach((r) => {
			if (!r?.qrCode) return;
			const existing = map.get(r.qrCode);
			const isActive = !!r.isActiveStock;
			if (existing) {
				existing.isActiveStock = existing.isActiveStock || isActive;
				existing.count += 1;
			} else {
				map.set(r.qrCode, { qrCode: r.qrCode, isActiveStock: isActive, count: 1 });
			}
		});
		return Array.from(map.values()).sort((a, b) => {
			if (a.isActiveStock !== b.isActiveStock) return a.isActiveStock ? -1 : 1;
			return a.qrCode.localeCompare(b.qrCode);
		});
	}, [rows]);

	const toggleChip = (qrCode) => {
		setSelectedQrs((prev) => {
			const next = new Set(prev);
			if (next.has(qrCode)) next.delete(qrCode);
			else next.add(qrCode);
			return next;
		});
	};

	const clearChips = () => setSelectedQrs(new Set());

	// Filtered + id-desc-ordered rows. BE already orders by activity id
	// desc — sort again client-side to keep the order stable if the FE
	// ever shuffles rows for any reason.
	const visibleRows = useMemo(() => {
		const filtered = selectedQrs.size === 0
			? rows
			: (rows || []).filter((r) => selectedQrs.has(r.qrCode));
		return [...filtered].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0));
	}, [rows, selectedQrs]);

	// Set of activity ids that should display the green dot — the LATEST
	// activity per currently-in-pack stockId. A single active stockId can
	// have many historical activities (stock-in, audit, etc.); marking
	// every one with a dot is noisy. Per-stockId max(id) gives one dot
	// per active child, on its most recent activity row.
	const activeStockLatestActivityIds = useMemo(() => {
		const latestIdByStock = new Map();
		(rows || []).forEach((r) => {
			if (!r?.isActiveStock || r?.stockId == null || r?.id == null) return;
			const prev = latestIdByStock.get(r.stockId);
			if (prev == null || r.id > prev) latestIdByStock.set(r.stockId, r.id);
		});
		return new Set(latestIdByStock.values());
	}, [rows]);

	const SPECIAL_ITEMS = {
		'Child QR': (qrCode, row) => (
			<div className='flex items-center gap-2'>
				{/* Green dot only on the LATEST activity per currently-in-pack
				    stockId — one dot per active child, on its most recent row.
				    Older activities of the same active stock (e.g., the original
				    stock-in row) stay clean to avoid visual noise. */}
				{activeStockLatestActivityIds.has(row?.id) && (
					<span
						className='inline-block w-2 h-2 rounded-full bg-green-500'
						title='Latest activity for a stock currently inside the pack'
					/>
				)}
				<a
					className='underline text-blue-400'
					href={`/console2/order/tracking?type=qrCode&value=${qrCode}`}
					target='_blank'
					rel='noreferrer'
				>
					{qrCode}
				</a>
			</div>
		),
		Movement: (movementType) => {
			const colorClass = movementType === 'Stocked In'
				? 'bg-green-100 text-green-800'
				: 'bg-red-100 text-red-800';
			return (
				<span className={`px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
					{movementType}
				</span>
			);
		},
		Qty: (qty) => <div className='text-right'>{qty ?? '—'}</div>,
		'Post Stock': (postUpdateStock) => <div className='text-right'>{postUpdateStock ?? '—'}</div>,
		Reason: (reason) => <div className='text-left'>{reason || '—'}</div>,
		Reference: (refType, row) => {
			if (!refType && !row?.refId) return '—';
			return (
				<div className='text-left text-xs text-gray-700'>
					{refType}{row?.refId != null ? ` #${row.refId}` : ''}
				</div>
			);
		},
		Note: (shortInfo) => <div className='text-left text-xs text-gray-600'>{shortInfo || '—'}</div>,
		'Activity Date': (activityDate) => (
			<div className='w-[150px]'>
				{activityDate ? moment(activityDate).format('YYYY-MM-DD HH:mm') : '—'}
			</div>
		),
		'Activity By': (activityBy) => <div className='text-left'>{activityBy || '—'}</div>,
	};

	const getRowStyles = (ele) => {
		if (ele?.movementType === 'Stocked In') return 'bg-[#00ff0d20]';
		if (ele?.movementType === 'Stocked Out') return 'bg-[#ff0c0c20]';
		return '';
	};

	const heading = packageQr
		? `Child Activities — ${packageQr}`
		: 'Child Activities';

	return (
		<BaseModal
			open={show}
			heading={heading}
			handleClose={handleClose}
			showConfirm={false}
			width='75rem'
			height='35rem'
			wrapperClassName='!overflow-y-hidden'
		>
			<BackdropLoader isLoading={loading} showProgress />
			<div className='flex items-center gap-4 px-4 py-2 border-b border-gray-200 text-sm text-gray-600'>
				<div className='flex items-center gap-1'>
					<span className='inline-block w-2 h-2 rounded-full bg-green-500' />
					<span>Currently in pack: <b>{counts.activeStockCount}</b></span>
				</div>
				<div>Total child QRs ever mapped: <b>{counts.totalChildCount}</b></div>
				<div className='ml-auto'>
					Activities: <b>{visibleRows.length}</b>
					{selectedQrs.size > 0 && (
						<span className='text-gray-400'> / {rows.length}</span>
					)}
				</div>
			</div>
			{/* Child-QR chip strip. Multi-select filter:
			    - empty selection ⇒ show all activities (default)
			    - one or more selected ⇒ show only matching qrCodes
			    Active chips (currently mapped to pack) get a green dot
			    accent to match the row indicator. */}
			{childQrChips.length > 0 && (
				<div className='flex items-center flex-wrap gap-1.5 px-4 py-2 border-b border-gray-200 bg-gray-50'>
					<span className='text-xs text-gray-500 mr-1'>Filter by QR:</span>
					{childQrChips.map((chip) => {
						const isSelected = selectedQrs.has(chip.qrCode);
						const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-pointer transition-colors';
						const cls = isSelected
							? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600'
							: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100';
						return (
							<button
								type='button'
								key={chip.qrCode}
								className={`${base} ${cls}`}
								onClick={() => toggleChip(chip.qrCode)}
								title={`${chip.count} activit${chip.count === 1 ? 'y' : 'ies'}`}
							>
								{chip.isActiveStock && (
									<span
										className={`inline-block w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-green-200' : 'bg-green-500'}`}
									/>
								)}
								<span>{chip.qrCode}</span>
								<span className={isSelected ? 'text-blue-100' : 'text-gray-400'}>
									({chip.count})
								</span>
							</button>
						);
					})}
					{selectedQrs.size > 0 && (
						<button
							type='button'
							className='ml-2 text-xs text-blue-500 hover:text-blue-700 underline'
							onClick={clearChips}
						>
							Clear ({selectedQrs.size})
						</button>
					)}
				</div>
			)}
			<div className='w-full overflow-auto !text-xs' style={{ maxHeight: '26rem' }}>
				<ListTable
					allowCheckList={false}
					colMap={COLMAP}
					columns={TABLE_COLUMNS}
					data={visibleRows.map((ele) => ({
						...ele,
						rowStyles: getRowStyles(ele),
					}))}
					special={{ ...SPECIAL_ITEMS }}
					fixedHeaderTop={0}
				/>
			</div>
		</BaseModal>
	);
};

const mapStateToProps = (state) => {
	const { show, data } = state?.modal?.childQrActivitiesModal ?? { show: false, data: {} };
	return { show, data };
};

const mapDispatchToProps = {
	toggleModal: modalActions.toggleChildQrActivitiesModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChildQrActivitiesModal);
